import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Container } from '@mui/material';
import { RootState, AppDispatch } from '../../store';

/**
 * Dashboard Page
 * Redirects to appropriate dashboard based on user role.
 *
 * @author Civilhealth Development Team
 */
const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Debug log
    console.log('DashboardPage - isAuthenticated:', isAuthenticated);
    console.log('DashboardPage - user:', user);

    // Wait for authentication to be determined
    if (!isAuthenticated) {
      console.log('DashboardPage - Not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    // Wait for user data to be loaded
    if (!user) {
      console.log('DashboardPage - Waiting for user data');
      return;
    }

    // Redirect based on role
    const role = user.role;
    console.log('DashboardPage - User role:', role);

    switch (role) {
      case 'CIVILHEALTH_COORDINATOR':
      case 'SYSTEM_ADMIN':
        console.log('DashboardPage - Redirecting to admin-dashboard');
        navigate('/admin-dashboard', { replace: true });
        break;
      case 'GOVERNMENT_DOCTOR':
        console.log('DashboardPage - Redirecting to doctor-dashboard');
        navigate('/doctor-dashboard', { replace: true });
        break;
      case 'LOKBHAVAN_STAFF':
        console.log('DashboardPage - Redirecting to staff-dashboard');
        navigate('/staff-dashboard', { replace: true });
        break;
      case 'OFFICE_ADMIN':
        console.log('DashboardPage - Redirecting to office-dashboard');
        navigate('/office-dashboard', { replace: true });
        break;
      case 'LAB_ADMIN':
        console.log('DashboardPage - Redirecting to lab-dashboard');
        navigate('/lab-dashboard', { replace: true });
        break;
      case 'IAS_OFFICER':
      case 'IPS_OFFICER':
      case 'IFS_OFFICER':
      case 'IRS_OFFICER':
      case 'JUDGE':
      case 'MINISTER':
      case 'OSD':
      case 'SENIOR_GOVERNMENT_OFFICER':
        console.log('DashboardPage - Redirecting to officer-dashboard');
        navigate('/officer-dashboard', { replace: true });
        break;
      default:
        console.warn('DashboardPage - Unknown role:', role);
        navigate('/login', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <Container sx={{ textAlign: 'center' }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography variant="h6">Redirecting to dashboard...</Typography>
      </Container>
    </Box>
  );
};

export default DashboardPage;